import { useState, useEffect } from 'react';

/**
 * How the app is being run. `paired`: the tablet is bound to a court by the center manager
 * (see useCourtPairing) and runs as a kiosk, one loop from the waiting screen to the result
 * and back. `standalone`: a link, a QR code or a manual match, the referee owns the tablet.
 */
export type AppMode = 'standalone' | 'paired';

/** Burger menu entries that a mode can hide. */
export interface MenuVisibility {
  language: boolean;
  fullscreen: boolean;
  rules: boolean;
  quiz: boolean;
  version: boolean;
  editMatch: boolean;
  newMatch: boolean;
}

export interface AppConfig {
  defaultCallbackUrl: string;
  /** Menu entries per mode. A partial object in config.json completes the defaults. */
  ui: Record<AppMode, MenuVisibility>;
  paired: {
    /**
     * Seconds a paired tablet stays on the result screen before it goes back to its waiting
     * screen on its own (paused while the validation is being sent and while the
     * self-assessment form is open). 0 disables the automatic return.
     */
    resultScreenTimeoutSeconds: number;
    /**
     * Whether the waiting screen offers "Enter a match manually" on the paired court. Off by
     * default: a kiosk tablet scores the matches the tournament system sends to its court.
     */
    allowManualMatch: boolean;
  };
}

const ALL_ENTRIES: MenuVisibility = {
  language: true, fullscreen: true, rules: true, quiz: true, version: true, editMatch: true, newMatch: true,
};

export const DEFAULT_CONFIG: AppConfig = {
  defaultCallbackUrl: '',
  ui: {
    standalone: { ...ALL_ENTRIES },
    // A kiosk tablet keeps what every referee needs (language, fullscreen, reference, quiz,
    // version) and loses the two gestures that leave the loop: the organizer resets the
    // tablet from the center manager instead.
    paired: { ...ALL_ENTRIES, editMatch: false, newMatch: false },
  },
  paired: {
    resultScreenTimeoutSeconds: 5 * 60,
    allowManualMatch: false,
  },
};

/**
 * config.json over the defaults, one level deep per section, so a file that only sets
 * `defaultCallbackUrl` (every deployment so far) changes nothing else, and a partial `ui`
 * section only overrides the entries it names.
 */
export function mergeConfig(data: any): AppConfig {
  const source = data && typeof data === 'object' ? data : {};
  const ui = source.ui && typeof source.ui === 'object' ? source.ui : {};
  const paired = source.paired && typeof source.paired === 'object' ? source.paired : {};
  const timeout = Number(paired.resultScreenTimeoutSeconds);
  return {
    defaultCallbackUrl: typeof source.defaultCallbackUrl === 'string' ? source.defaultCallbackUrl : DEFAULT_CONFIG.defaultCallbackUrl,
    ui: {
      standalone: { ...DEFAULT_CONFIG.ui.standalone, ...(ui.standalone || {}) },
      paired: { ...DEFAULT_CONFIG.ui.paired, ...(ui.paired || {}) },
    },
    paired: {
      resultScreenTimeoutSeconds: Number.isFinite(timeout) && timeout >= 0 ? timeout : DEFAULT_CONFIG.paired.resultScreenTimeoutSeconds,
      allowManualMatch: typeof paired.allowManualMatch === 'boolean' ? paired.allowManualMatch : DEFAULT_CONFIG.paired.allowManualMatch,
    },
  };
}

/** Menu entries to show in the given mode. */
export function getMenuVisibility(config: AppConfig, mode: AppMode): MenuVisibility {
  return config.ui[mode];
}

let cachedConfig: AppConfig | null = null;

/** Test hook: forget the loaded config so the next mount fetches it again. */
export function resetAppConfigCache(): void {
  cachedConfig = null;
}

/**
 * Hook pour charger la configuration externe de l'application
 * Le fichier config.json est chargé depuis le dossier public
 */
export function useAppConfig() {
  const [config, setConfig] = useState<AppConfig>(cachedConfig || DEFAULT_CONFIG);
  const [isLoading, setIsLoading] = useState(!cachedConfig);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (cachedConfig) {
      return;
    }

    const loadConfig = async () => {
      try {
        const response = await fetch(`${process.env.PUBLIC_URL}/config.json`);
        if (!response.ok) {
          throw new Error(`HTTP ${response.status}`);
        }
        const data = await response.json();
        cachedConfig = mergeConfig(data);
        setConfig(cachedConfig);
      } catch (err) {
        console.warn('Impossible de charger config.json:', err);
        setError((err as Error).message);
        cachedConfig = DEFAULT_CONFIG;
        setConfig(DEFAULT_CONFIG);
      } finally {
        setIsLoading(false);
      }
    };

    loadConfig();
  }, []);

  return { config, isLoading, error };
}
